import { CheckCircle2, XCircle } from "lucide-react";
import { JP } from "@/components/JP";
import { usePrefs } from "@/context/PrefsContext";

export function ScoreCard({ result, title, children }) {
  const { t } = usePrefs();
  return (
    <div data-testid="result-score-card" className="soft-card p-6 sm:p-8 fade-up">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{title}</p>
      <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-2">
        <div><span data-testid="result-score" className="font-jpserif text-6xl leading-none">{result.score}</span><span className="text-xl text-muted-foreground"> / {result.total}</span></div>
        <div className="pb-1 text-sm text-muted-foreground"><span data-testid="result-pct" className="font-semibold text-foreground">{result.pct}%</span> {t("correct").toLowerCase()}</div>
      </div>
      <div className="mt-5 h-2 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-sage transition-[width] duration-700" style={{ width: `${result.pct}%` }} /></div>
      {children}
    </div>
  );
}

export function Breakdown({ breakdown }) {
  const { t, area } = usePrefs();
  return (
    <div data-testid="result-breakdown" className="soft-card p-6 sm:p-8">
      <h3 className="text-base font-semibold">{t("breakdown")}</h3>
      <div className="mt-5 space-y-4">
        {breakdown.map((b) => (
          <div key={b.area} data-testid={`breakdown-${b.area}`}>
            <div className="mb-1.5 flex items-center justify-between text-sm"><span>{area(b.area)}</span><span className="text-muted-foreground">{b.correct}/{b.total} · <span className={b.pct < 70 ? "text-primary font-medium" : "text-sage font-medium"}>{b.pct}%</span></span></div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className={`h-full rounded-full ${b.pct < 70 ? "bg-primary/70" : "bg-sage"}`} style={{ width: `${b.pct}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReviewList({ items }) {
  const { t, lang } = usePrefs();
  return (
    <div className="space-y-4" data-testid="result-review-list">
      {items.map((it, i) => (
        <div key={it.question_id} data-testid={`review-item-${i}`} className={`soft-card border-l-4 p-5 sm:p-6 ${it.correct ? "border-l-sage" : "border-l-primary"}`}>
          <div className="mb-3 flex items-center gap-2 text-xs font-medium">
            {it.correct ? <CheckCircle2 className="h-4 w-4 text-sage" /> : <XCircle className="h-4 w-4 text-primary" />}
            <span className={it.correct ? "text-sage" : "text-primary"}>{it.correct ? t("correct") : t("wrong")}</span>
            <span className="text-muted-foreground">· {i + 1} · {it.level}</span>
          </div>
          <JP text={it.prompt_jp} as="p" className="text-lg" mode="all" />
          <p className="mt-1 text-sm text-muted-foreground">{it.prompt_id}</p>
          <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
            <div className="rounded-lg bg-muted/60 px-3 py-2"><span className="text-xs text-muted-foreground">{t("yourAnswer")}: </span><span lang="ja" className="jp">{it.chosen ? it.options.find((o) => o.key === it.chosen)?.text : "—"}</span></div>
            <div className="rounded-lg bg-sage-soft px-3 py-2"><span className="text-xs text-muted-foreground">{t("correctAnswer")}: </span><span lang="ja" className="jp">{it.options.find((o) => o.key === it.answer)?.text}</span></div>
          </div>
          <div className="mt-4 border-t border-border pt-3 text-sm leading-relaxed">
            <span className="mr-2 text-xs uppercase tracking-wide text-muted-foreground">{t("explanation")}</span>
            <p data-testid={`review-explanation-${i}`}>{lang === "jp" ? it.explanation_jp : it.explanation_id}</p>
            <p lang="ja" className="jp mt-1 text-xs text-muted-foreground">{lang === "jp" ? it.explanation_id : it.explanation_jp}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
